import {
  Box,
  Button,
  Checkbox,
  ColumnDatas,
  getColor,
  Icons,
  Modal,
  Radio,
  Table,
  Tabs,
  Tree,
  Typography,
  useToast,
} from "fds";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { nanoid } from "@reduxjs/toolkit";
import { Workgroup } from "../../types/workgroup";
import utilsCommon from "../../shared/utils/common";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import useAdminDeptStore from "../../redux/dispatcher/useAdminDeptStore";

export type ModalProps = {
  workgroupInfo: Workgroup;
  showWorkgroupManagerModal: boolean;
  onClose: any;
};

const WorkgroupManagerModal: React.FC<ModalProps> = ({
  workgroupInfo,
  showWorkgroupManagerModal,
  onClose,
}) => {
  const { changeManager } = useAdminWorkgroupStore();
  const { adminDeptStore, getDeptDetail, getDeptUsers } = useAdminDeptStore();

  const { t } = useTranslation();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [managerId, setManagerId] = useState("");
  const [selectInternalMember, setSelectInternalMember] = useState("");
  const [expandValues, setExpandValues] = useState([
    "ae4fc0f5fd8f4142834c1ad4e929a514",
  ] as string[]);

  useEffect(() => {
    if (workgroupInfo.managerId) {
      setManagerId(workgroupInfo.managerId);
    }
    getDeptDetail("ae4fc0f5fd8f4142834c1ad4e929a514");
    getDeptUsers("ae4fc0f5fd8f4142834c1ad4e929a514");
  }, [workgroupInfo]);

  const searchTree = (value: string): void => {
    getDeptDetail(value);
    getDeptUsers(value);
  };

  function transformFileList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: (
                <Radio
                  check={item.userId === managerId}
                  onClick={() => setManagerId(item.userId)}
                />
              ),
            },
            {
              columnText: utilsCommon.getNameWithId(item.userName, item.userId),
            },
            { columnText: item.deptName },
          ],
          id: item.userId,
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  return (
    <Modal
      open={showWorkgroupManagerModal}
      onClose={onClose}
      footer={
        <Box justifyContent={"flex-end"}>
          <Button
            btnStyle="fill"
            label={t("admin.common.button.cancel")}
            type="secondary"
            onClick={() => onClose()}
          />
          <Button
            btnStyle="fill"
            label={t("admin.common.button.ok")}
            state={
              tab === 0
                ? workgroupInfo.managerId === managerId
                  ? "disable"
                  : "default"
                : selectInternalMember === ""
                ? "disable"
                : "default"
            }
            onClick={async () => {
              let result: any = await changeManager({
                workgroupId: workgroupInfo.workgroupId,
                managerId: tab === 0 ? managerId : selectInternalMember,
              });
              if (result.error) {
                toast.toastMsg(nanoid(), result.error.message, "error");
              } else {
                toast.toastMsg(nanoid(), t("common.alert.modify"), "success");
              }
              onClose();
            }}
          />
        </Box>
      }
      height={700}
      title={t("admin.workgroup.managerChange")}
      body={
        <Box
          direction="column"
          height={528}
          classes="adminWorkgroupChangeModal"
        >
          <Box alignItems="center">
            <Typography type="subtitle2">
              {t("admin.workgroup.chosenWorkgroup")}
            </Typography>
            <Typography
              type="subtitle2"
              classes="mg ml-4"
              exactColor={getColor("COLOR_TEXT_BRAND")}
            >
              {workgroupInfo.workgroupName}
            </Typography>
            <Typography type="subtitle2">
              {t("admin.workgroup.managerMessageRest")}
            </Typography>
          </Box>
          <Box classes={"adminWorkgroupChangeModal-master"}>
            <Icons
              variant="line"
              label="crown"
              stroke={
                window.eco.companyName === "workspace" ? "#6623e8" : "#25c16f"
              }
            />
            <Typography type="body2_md" classes="mg ml-8">
              {utilsCommon.getNameWithId(
                workgroupInfo.managerName,
                workgroupInfo.managerId
              )}
            </Typography>
          </Box>
          <Box classes="mg mt-12">
            <Tabs
              tabs={[
                {
                  color: "default",
                  isNew: false,
                  text: t("admin.workgroup.workgroupMember"),
                },
                {
                  color: "default",
                  isNew: false,
                  text: t("admin.dept.internalUser"),
                },
              ]}
              selectTab={(value) => {
                setTab(value);
              }}
              selected={tab}
              size={"md"}
            />
          </Box>

          {tab === 0 && (
            <Box classes="mg mt-12" direction="column">
              <Table
                width={"100%"}
                height={"360px"}
                header={[
                  {
                    headerText: "",
                    width: "50px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.adminSetting.userNameId"),
                    width: "300px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.workgroup.dept"),
                    width: "250px",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformFileList(workgroupInfo.members)}
                totalCount={workgroupInfo.members.length}
                columnHeight="md"
                endReached={() => {}}
              />
            </Box>
          )}
          {tab === 1 && (
            <Box classes="adminWorkgroupChangeModal-tree">
              <Tree
                width="100%"
                treeData={{
                  columnText: t("admin.dept.internalUser"),
                  subTree: [...adminDeptStore.internalTree.subTree],
                  showChildren: true,
                  hasChildren: true,
                  value: "ae4fc0f5fd8f4142834c1ad4e929a514",
                  type: "company",
                }}
                expandAction={(value: string) => {
                  if (expandValues.includes(value)) {
                    setExpandValues(
                      expandValues.filter((item) => item !== value)
                    );
                  } else {
                    setExpandValues([...expandValues, value]);
                    searchTree(value);
                  }
                }}
                depth={0}
                isOnlyUser
                checkType="radio"
                treeType="eco"
                checkValues={[selectInternalMember]}
                checkAction={(value: any) => {
                  setSelectInternalMember(value.value);
                }}
                expandValues={expandValues}
                expandSearchValues={[]}
              />
            </Box>
          )}
        </Box>
      }
    />
  );
};
export default WorkgroupManagerModal;
